// OvermindGob — the engine AI for computer players: a bodiless state-machine gob (at
// 0,0) that on init sends every owned miner mining, then pulses — every 125 ticks pick
// ONE idle non-miner/non-MHQ pawn and aim it at a RANDOM enemy structure (count targets,
// GetRandom()%count, re-scan to that index — 1 sync-RNG draw when targets exist); every
// 375 ticks coin-flip VTS/HRC and queue a random unit from that builder's table (2
// draws). game/Overmind.cpp (all 300 lines; the STRESS arms are compiled out).

import type { SimContext } from '../context.ts';
import { Gob, kfGobStateMachine, kfGobActive, kfGobMobileUnit, kfGobStructure } from './gob.ts';
import { MobileUnitGob, kwxInvalid } from './mobileunit.ts';
import { UnitGob } from './unitgob.ts';
import { BuilderGob } from './builder.ts';
import {
  kgtOvermind,
  kgtGalaxMiner,
  kgtMobileHeadquarters,
  kgtVehicleTransportStation,
  kgtHumanResourceCenter,
} from './gobtypes.ts';
import {
  kutLightTank,
  kutMediumTank,
  kutRocketVehicle,
  kutMachineGunVehicle,
  kutArtillery,
  kutShortRangeInfantry,
  kutLongRangeInfantry,
} from '../player/side.ts';
import {
  Message,
  newMessage,
  knHandled,
  knNotHandled,
  kstReservedGlobal,
  kmidReservedUpdate,
  kmidAttackAction,
  kmidMineCommand,
  kgidNull,
} from '../sm/message.ts';
import { knLimitMobileUnit } from './gobmgr.ts';
import { registerGobType } from './factory.ts';
import type { WPoint } from '../types.ts';
import type { IniReader } from '../data/ini.ts';

// game/Overmind.cpp:63-64
const s_autVtsBuildable = [
  kutLightTank,
  kutMediumTank,
  kutRocketVehicle,
  kutMachineGunVehicle,
  kutArtillery,
];
const s_autHrcBuildable = [kutShortRangeInfantry, kutLongRangeInfantry];

// game/Overmind.cpp:3-6 — nothing to configure
export function InitOvermindClass(_ctx: SimContext, _pini: IniReader): boolean {
  return true;
}

export class OvermindGob extends Gob {
  m_fEnabled = true;
  m_fInitializationComplete = false;
  m_cAttackCountdown = 0;
  m_cBuildUnitCountdown = 0;

  constructor(ctx: SimContext) {
    // Overmind.cpp:7-10
    super(ctx);
  }

  override GetType(): number {
    return kgtOvermind;
  }

  // game/Overmind.cpp:14-27
  InitOvermind(): boolean {
    this.m_wx = 0;
    this.m_wy = 0;
    this.m_ff = (this.m_ff | kfGobStateMachine) >>> 0;
    this.ctx.gobm.AddGob(this);
    this.m_fInitializationComplete = false;
    return true;
  }

  // game/Overmind.cpp:54-69 — the level-INI spawn path: the arg parse is compiled out
  // (#if 0); it just delegates to the plain Init
  InitFromArgs(_args: string): boolean {
    return this.InitOvermind();
  }

  // The Overmind is absent from the C++/faithful save format (the shipping game never
  // resumes a computer-overmind level mid-game), and the level RE-CREATES one per
  // kfPlrComputerOvermind player on load — so it stays unsavable in the gob list.
  IsSavable(): boolean {
    return false;
  }

  // issue #96 / replay-viewer seek: the Overmind DOES carry deterministic decision state
  // (the attack + build-pulse countdowns and the init flag) that drives the sync RNG — its
  // ProcessStateMachineMessage pulses consume ctx.rng. Because it is unsavable, a from-snapshot
  // resume re-created a FRESH Overmind whose countdowns/init differed from the from-seed sim, so
  // it pulsed on the wrong cadence and diverged the sim within a few blocks — every replay-viewer
  // checkpoint restore then failed hash validation and fell back to a full re-sim from seed
  // (very slow seeking). The COMPLETE snapshot carries these four fields and re-applies them to
  // the level-recreated Overmind; m_unvl scheduling needs no capture (UpdateInterval.Decrement is
  // a no-op in multiplayer, so the pulse fires every update deterministically).
  SnapshotDecisionState(pstm: import('../sm/stream.ts').Stream): void {
    pstm.WriteByte(this.m_fEnabled ? 1 : 0);
    pstm.WriteByte(this.m_fInitializationComplete ? 1 : 0);
    pstm.WriteDword(this.m_cAttackCountdown | 0);
    pstm.WriteDword(this.m_cBuildUnitCountdown | 0);
  }
  RestoreDecisionState(pstm: import('../sm/stream.ts').Stream): void {
    this.m_fEnabled = pstm.ReadByte() !== 0;
    this.m_fInitializationComplete = pstm.ReadByte() !== 0;
    this.m_cAttackCountdown = pstm.ReadDword() | 0;
    this.m_cBuildUnitCountdown = pstm.ReadDword() | 0;
  }

  // game/Overmind.cpp:58-62 — trigger toggle
  Toggle(): void {
    this.m_fEnabled = !this.m_fEnabled;
    this.m_unvl.MinSkip();
  }

  // game/Overmind.cpp:65-280
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st !== kstReservedGlobal || pmsg.mid !== kmidReservedUpdate) return knNotHandled;

    if (!this.m_fInitializationComplete) {
      this.m_fInitializationComplete = true;
      this.m_cAttackCountdown = 0;
      this.m_cBuildUnitCountdown = 0;
      // Send every owned miner mining
      for (
        let pgobMiner = this.ctx.gobm.GetFirstGob();
        pgobMiner !== null;
        pgobMiner = this.ctx.gobm.GetNextGob(pgobMiner)
      ) {
        if (pgobMiner.GetOwner() !== this.m_pplr) continue;
        if (pgobMiner.GetType() !== kgtGalaxMiner) continue;
        const msg = newMessage(kmidMineCommand, kgidNull, pgobMiner.GetId());
        msg.MineCommand.gidTarget = kgidNull;
        msg.MineCommand.wptTarget.wx = kwxInvalid;
        msg.MineCommand.wptTarget.wy = 0;
        this.ctx.smm!.SendMsg(msg);
      }
    }
    if (!this.m_fEnabled) return knHandled;

    // --- attack pulse: every 12.5s pick ONE idle pawn and aim it at a random enemy structure
    this.m_cAttackCountdown -= this.m_unvl.GetUpdateCount();
    if (this.m_cAttackCountdown < 0) {
      this.m_cAttackCountdown = Math.trunc(12.5 * 10);

      for (
        let pgobPawn = this.ctx.gobm.GetFirstGob();
        pgobPawn !== null;
        pgobPawn = this.ctx.gobm.GetNextGob(pgobPawn)
      ) {
        if (pgobPawn.GetOwner() !== this.m_pplr) continue;
        const ff = pgobPawn.GetFlags();
        if ((ff & (kfGobActive | kfGobMobileUnit)) !== (kfGobActive | kfGobMobileUnit) >>> 0)
          continue;
        const pmuntPawn = pgobPawn as MobileUnitGob;
        if (!pmuntPawn.IsIdle()) continue;
        if (pmuntPawn.HasAttackTarget()) continue;
        const gt = pmuntPawn.GetType();
        if (gt === kgtGalaxMiner || gt === kgtMobileHeadquarters) continue;

        // Count valid enemy structures, then walk to a random one of them
        let cTargets = 0;
        let pgobTarget: Gob | null = null;
        for (
          pgobTarget = this.ctx.gobm.GetFirstGob();
          pgobTarget !== null;
          pgobTarget = this.ctx.gobm.GetNextGob(pgobTarget)
        ) {
          if (
            pgobTarget.GetOwner() !== this.m_pplr &&
            (pgobTarget.GetFlags() & kfGobStructure) === kfGobStructure >>> 0 &&
            pmuntPawn.IsValidTarget(pgobTarget)
          ) {
            cTargets++;
          }
        }
        if (cTargets !== 0) {
          cTargets = this.ctx.rng.GetRandom() % cTargets; // SYNC RNG
          for (
            pgobTarget = this.ctx.gobm.GetFirstGob();
            pgobTarget !== null;
            pgobTarget = this.ctx.gobm.GetNextGob(pgobTarget)
          ) {
            if (
              pgobTarget.GetOwner() !== this.m_pplr &&
              (pgobTarget.GetFlags() & kfGobStructure) === kfGobStructure >>> 0 &&
              pmuntPawn.IsValidTarget(pgobTarget)
            ) {
              if (cTargets-- === 0) break;
            }
          }
        }
        if (pgobTarget === null) break; // (also covers cTargets==0: the walk found nothing)

        const puntTarget = pgobTarget as UnitGob;
        const msgT = newMessage(kmidAttackAction, pmuntPawn.GetId(), pmuntPawn.GetId());
        const wpt: WPoint = { wx: 0, wy: 0 };
        puntTarget.GetAttackPoint(wpt);
        msgT.AttackCommand.wptTarget.wx = wpt.wx;
        msgT.AttackCommand.wptTarget.wy = wpt.wy;
        msgT.AttackCommand.gidTarget = puntTarget.GetId();
        msgT.AttackCommand.wptTargetCenter.wx = wpt.wx;
        msgT.AttackCommand.wptTargetCenter.wy = wpt.wy;
        msgT.AttackCommand.tcTargetRadius = 0;
        msgT.AttackCommand.wcMoveDistPerUpdate = 0;
        this.ctx.smm!.SendMsg(msgT);
        break;
      }
    }
    this.m_unvl.MinSkip(this.m_cAttackCountdown);

    // --- build pulse: every 30s coin-flip a builder type and queue a random unit
    this.m_cBuildUnitCountdown -= this.m_unvl.GetUpdateCount();
    if (this.m_cBuildUnitCountdown < 0) {
      this.m_cBuildUnitCountdown = Math.trunc(12.5 * 30);

      let gtBuilder: number;
      let utBuildee: number;
      if ((this.ctx.rng.GetRandom() & 1) === 1) {
        // SYNC RNG
        gtBuilder = kgtVehicleTransportStation;
        utBuildee = s_autVtsBuildable[this.ctx.rng.GetRandom() % s_autVtsBuildable.length]; // SYNC RNG
      } else {
        gtBuilder = kgtHumanResourceCenter;
        utBuildee = s_autHrcBuildable[this.ctx.rng.GetRandom() % s_autHrcBuildable.length]; // SYNC RNG
      }
      if (
        this.ctx.gobm.IsBelowLimit(
          knLimitMobileUnit,
          this.m_pplr as import('../player/player.ts').Player,
        )
      ) {
        for (
          let pgobBuilder = this.ctx.gobm.GetFirstGob();
          pgobBuilder !== null;
          pgobBuilder = this.ctx.gobm.GetNextGob(pgobBuilder)
        ) {
          if (pgobBuilder.GetOwner() !== this.m_pplr) continue;
          if (pgobBuilder.GetType() !== gtBuilder) continue;
          const pbld = pgobBuilder as BuilderGob;
          if (pbld.IsBuildInProgress()) continue;
          pbld.Build(utBuildee);
          break;
        }
        // (the STRESS spawn-anywhere arm is compiled out)
      }
    }
    this.m_unvl.MinSkip(this.m_cBuildUnitCountdown);
    return knHandled;
  }
}

// self-register (see factory.ts)
registerGobType(kgtOvermind, (ctx) => new OvermindGob(ctx));
